import { useEffect } from "react";
import {
  mapDetectionsToGeo,
  mapSegmentationsToGeo,
  getAreaGeoContext,
} from "./aiMappingDetections";
import { AiMappingAreaLike, AiMappingResultsLike } from "./aiMappingTypes";

interface UseAiMappingMapSyncOptions {
  driver: {
    syncAreas: (
      areas: AiMappingAreaLike[],
      selectedAreaId: string | null,
      onSelectArea?: (areaId: string) => void,
      onClearArea?: () => void
    ) => void;
    syncDetections: (
      detections: ReturnType<typeof mapDetectionsToGeo>,
      selectedDetectionId: string | null,
      onSelect?: (detectionId: string, areaId?: string) => void
    ) => void;
    syncSegmentations: (
      segmentations: ReturnType<typeof mapSegmentationsToGeo>,
      visible: boolean,
      selectedSegmentationId: string | null,
      onSelectSegmentation?: (segmentationId: string, areaId?: string) => void
    ) => void;
    setClickHandler: (
      handler: ((latLng: import("./aiMappingGeo").LatLngPair) => void) | null
    ) => void;
  } | null;
  areas: AiMappingAreaLike[];
  selectedAreaId: string | null;
  allResults: AiMappingResultsLike[];
  detectionsVisible: boolean;
  segmentationsVisible: boolean;
  selectedDetectionId: string | null;
  selectedSegmentationId: string | null;
  isDetectionGroupVisible: (category: string) => boolean;
  selectArea: (areaId: string | null) => void | Promise<void>;
  selectDetection: (detectionId: string | null) => void;
  selectSegmentation: (segmentationId: string | null) => void;
  onSegmentationClick: (segmentationId: string, areaId: string) => void;
}

export function useAiMappingMapSync({
  driver,
  areas,
  selectedAreaId,
  allResults,
  detectionsVisible,
  segmentationsVisible,
  selectedDetectionId,
  selectedSegmentationId,
  isDetectionGroupVisible,
  selectArea,
  selectDetection,
  selectSegmentation,
  onSegmentationClick,
}: UseAiMappingMapSyncOptions) {
  useEffect(() => {
    driver?.syncAreas(
      areas,
      selectedAreaId,
      (areaId) => void selectArea(areaId),
      () => void selectArea(null)
    );
  }, [driver, areas, selectedAreaId, selectArea]);

  useEffect(() => {
    if (!driver) return;
    if (allResults.length > 0) {
      driver.syncDetections(
        detectionsVisible
          ? allResults.flatMap((result) =>
              mapDetectionsToGeo(
                result.detections.filter((detection) =>
                  isDetectionGroupVisible(detection.category)
                ),
                getAreaGeoContext(result.area)
              ).map((detection) => ({ ...detection, areaId: result.area.id }))
            )
          : [],
        selectedDetectionId,
        (detectionId, areaId) => {
          if (areaId) void selectArea(areaId);
          selectDetection(detectionId);
        }
      );
    } else {
      driver.syncDetections([], null);
    }
  }, [
    driver,
    allResults,
    detectionsVisible,
    selectedDetectionId,
    selectDetection,
    isDetectionGroupVisible,
  ]);

  useEffect(() => {
    if (!driver) return;
    if (allResults.length > 0) {
      driver.syncSegmentations(
        allResults.flatMap((result) =>
          mapSegmentationsToGeo(result.segmentations, getAreaGeoContext(result.area)).map(
            (segmentation) => ({ ...segmentation, areaId: result.area.id })
          )
        ),
        segmentationsVisible,
        selectedSegmentationId,
        (segmentationId, areaId) => {
          if (areaId) onSegmentationClick(segmentationId, areaId);
        }
      );
    } else {
      driver.syncSegmentations([], false, null);
    }
  }, [driver, allResults, segmentationsVisible, selectedSegmentationId, onSegmentationClick]);

  useEffect(
    () => () => {
      driver?.setClickHandler(null);
    },
    [driver]
  );
}
